import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../api/apiClient';

interface Document {
  _id: string;
  title: string;
  category: string;
  description?: string;
  fileUrl: string;
  mimeType?: string;
  originalName: string;
  uploadedBy: {
    _id: string;
    name: string;
  };
  createdAt: string;
  version?: string;
  expiryDate?: string;
  visibility?: 'Visible to Employees' | 'Admin Only';
  requireAcknowledgment?: boolean;
  acknowledgments?: {
    employeeId: string;
    acknowledgedAt: string;
  }[];
}

interface DocumentState {
  documents: Document[];
  loading: boolean;
  error: string | null;
  uploading: boolean;
}

const initialState: DocumentState = {
  documents: [],
  loading: false,
  error: null,
  uploading: false,
};

export const fetchDocuments = createAsyncThunk('documents/fetchAll', async (companyId: string | undefined, { rejectWithValue }) => {
  try {
    const url = companyId ? `/documents?companyId=${companyId}` : '/documents';
    const { data } = await apiClient.get(url);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch documents');
  }
});

export const uploadDocument = createAsyncThunk('documents/upload', async (formData: FormData, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.post('/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to upload document');
  }
});

export const editDocument = createAsyncThunk('documents/edit', async ({ id, formData }: { id: string, formData: FormData }, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.put(`/documents/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update document');
  }
});

export const deleteDocument = createAsyncThunk('documents/delete', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/documents/${id}`);
    return id;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete document');
  }
});

export const acknowledgeDocument = createAsyncThunk('documents/acknowledge', async (id: string, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.put(`/documents/${id}/acknowledge`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to acknowledge document');
  }
});

const documentSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadDocument.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploading = false;
        state.documents.unshift(action.payload);
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload as string;
      })
      .addCase(editDocument.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(editDocument.fulfilled, (state, action) => {
        state.uploading = false;
        const index = state.documents.findIndex((doc: any) => (doc._id || doc.id) === (action.payload._id || action.payload.id));
        if (index !== -1) {
          state.documents[index] = action.payload;
        }
      })
      .addCase(editDocument.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.documents = state.documents.filter((doc: any) => (doc._id || doc.id) !== action.payload);
      })
      .addCase(acknowledgeDocument.fulfilled, (state, action) => {
        const index = state.documents.findIndex((doc: any) => (doc._id || doc.id) === (action.payload._id || action.payload.id));
        if (index !== -1) {
          state.documents[index] = action.payload;
        }
      });
  }
});

export const { clearError } = documentSlice.actions;
export default documentSlice.reducer;
